import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/hooks/use-profile";

export default function KeepLoggedInPrompt() {
  const { user, loading } = useProfile();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only verify once we know they are logged in
    if (!loading && user) {
      // Check if we already asked them
      const decided = localStorage.getItem('keep_logged_in_decided');
      if (!decided) {
        setOpen(true);
      }
    }
  }, [user, loading]);

  const handleDecision = async (keep: boolean) => {
    try {
      // Save their actual preference for customStorage
      localStorage.setItem('keep_logged_in', keep ? 'true' : 'false');
      // Save a flag so we don't ask them again on refresh
      localStorage.setItem('keep_logged_in_decided', 'true');

      // Blindly copy any Supabase token to the correct final storage location  
      // This uses ZERO network calls so it cannot freeze or crash the UI       
      if (keep) {
        // Move from Session to Local
        const keys = Object.keys(sessionStorage);
        for (const key of keys) {
          if (key.startsWith('sb-')) {
            const val = sessionStorage.getItem(key);
            if (val) {
              localStorage.setItem(key, val);
              sessionStorage.removeItem(key);
            }
          }
        }
      } else {
        // Move from Local to Session
        const keys = Object.keys(localStorage);
        for (const key of keys) {
          if (key.startsWith('sb-')) {
            const val = localStorage.getItem(key);
            if (val) {
              sessionStorage.setItem(key, val);
              localStorage.removeItem(key);
            }
          }
        }
      }
    } catch (e) {
      console.error('Failed to move tokens:', e);
    }
    
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      // Force user to pick an option by disallowing closing
      if (!isOpen) return;
    }}>
      <DialogContent className="sm:max-w-md [&>button]:hidden">
        <DialogHeader>
          <DialogTitle>Stay logged in?</DialogTitle>
          <DialogDescription>
            Would you like to stay signed in? Select 'No' if you are using a shared or public computer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0 mt-4">
          <Button variant="outline" onClick={() => handleDecision(false)}>      
            No, just for this session
          </Button>
          <Button onClick={() => handleDecision(true)}>
            Yes, keep me logged in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
